import { Button } from '@/components/base/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/base/dialog'
import type { IconTagName } from '@/components/common/icons/Icon'
import { Icon } from '@/components/common/icons/Icon'
import { useUiStore } from '@/stores/ui'
import type { ReactNode } from 'react'
import { forwardRef, useEffect, useState } from 'react'

interface BaseDialogSimpleProps {
  dialogDescription?: ReactNode
  dialogTitle?: string
  open: boolean
  tag: IconTagName
}

type DialogSimplePropsWithConfirm = BaseDialogSimpleProps & {
  confirmLabel: string
  onConfirm: () => void
}

type DialogSimplePropsWithoutConfirm = BaseDialogSimpleProps & {
  confirmLabel?: never
  onConfirm?: never
}

type DialogSimplePropsWithCancel = BaseDialogSimpleProps & {
  cancelLabel: string
  onCancel: () => void
}

type DialogSimplePropsWithoutCancel = BaseDialogSimpleProps & {
  cancelLabel?: never
  onCancel?: never
}

type DialogSimpleProps = (
  | DialogSimplePropsWithCancel
  | DialogSimplePropsWithoutCancel
) &
  (DialogSimplePropsWithConfirm | DialogSimplePropsWithoutConfirm)

const DialogSimple = forwardRef<HTMLDivElement, DialogSimpleProps>(
  (
    {
      cancelLabel,
      confirmLabel,
      dialogDescription,
      dialogTitle,
      onCancel,
      onConfirm,
      open,
      tag,
    },
    ref,
  ) => {
    const rootElementRef =
      useUiStore(state => state.rootElementRef) ?? document.body // the default fallback is document.body

    const [isOpen, setIsOpen] = useState(open)

    // Update the dialog state when the open prop changes
    useEffect(() => {
      setIsOpen(open)
    }, [open])

    const onOpenChange = (open: boolean) => {
      setIsOpen(open)
    }

    const handleCancel = () => {
      onCancel?.()
      onOpenChange(false)
    }

    const handleClose = () => {
      onConfirm?.()
    }

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          container={rootElementRef}
          ref={ref}
          // TODO: should we use onClose or onConfirm?
          onClose={handleClose}
        >
          <DialogHeader>
            <div className="flex flex-col items-center justify-center gap-y-8">
              <Icon className="h-20 w-20 fill-muted" tag={tag} />
              <DialogTitle>{dialogTitle}</DialogTitle>
            </div>
            {dialogDescription ? (
              <DialogDescription>{dialogDescription}</DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <DialogClose onClick={handleClose}>{confirmLabel}</DialogClose>
            {cancelLabel ? (
              <Button onClick={handleCancel} variant="destructive-text">
                Cancel
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  },
)

DialogSimple.displayName = 'DialogSimple'
export { DialogSimple }
